/**
 * ruleExpression.js — render a compiled check as the query it actually runs.
 *
 * Lives in utils, not in a component, because it belongs to the EDIT view (the
 * Rules page: what does this rule look for) rather than to the verdict view
 * (the Checks page: what did this session do). Those are different questions
 * and they had drifted into one panel.
 *
 * Everything here is derived from the check itself, so it cannot fall out of
 * step with what the engine runs.
 */

// Where a value is read from, said the way you would go looking for it.
export function describeSource(source, param) {
  const p = param == null ? '' : String(param);
  switch (source) {
    case 'url': return p ? `?${p}=` : 'url';
    // `find` looks in the query string AND the POST body — the distinction is
    // load-bearing for DAI/CTV, where vendors move the same parameter between
    // the two, so it must survive the shortening.
    case 'find': return p ? `?${p}= anywhere` : 'url or body';
    case 'custParams': return p ? `cust_params.${p}` : 'cust_params';
    case 'body': return p ? `body.${p}` : 'body';
    case 'response': return p ? `response.${p}` : 'response body';
    case 'validData': return p ? `validData.${p}` : 'validData';
    case 'issues': return p ? `finding ${p}` : 'validator findings';
    case 'status': return 'HTTP status';
    case 'iu': return 'ad unit';
    case 'host': return 'host';
    case 'sdk': return p ? `sdk.${p}` : 'SDK';
    case 'slot': return p ? `slot.${p}` : 'slot';
    case 'secureSignals': return p ? `secureSignals.${p}` : 'secure signals';
    case 'adTag': return p ? `adTag.${p}` : 'ad tag';
    case 'timing': return p ? `timing.${p}` : 'timing';
    default: return [source, p].filter(Boolean).join('.');
  }
}

// What has to be true of that value.
export function describeExpectation(a) {
  if (!a) return '—';
  const val = (v) => (Array.isArray(v) ? v.join(', ') : String(v));
  switch (a.op) {
    case 'present': return 'present';
    case 'absent': return 'absent';
    case 'equals': return `= ${val(a.value)}`;
    case 'oneOf': return `one of [${val(a.values)}]`;
    case 'matches': return `~ /${a.pattern}/`;
    case 'notMatches': return `!~ /${a.pattern}/`;
    case 'gt': return `> ${val(a.value)}`;
    case 'lt': return `< ${val(a.value)}`;
    case 'range': return `between ${val(a.min)} and ${val(a.max)}`;
    case 'inBucket': return 'on a standard price-bucket grid';
    // The vendor knowledge pack owns the allowed values, so the rule stays
    // correct when the pack is updated instead of hard-coding a list here.
    case 'validValue': return 'valid per the vendor spec';
    default: return a.op ? `op: ${a.op}` : '—';
  }
}

// How many rows have to satisfy it. This is the part people get wrong reading
// a rule name: `exists` passing tells you ONE request carried the value, not
// that they all did.
export const MODE_GLOSS = {
  each: 'on every request',
  all: 'on every request',
  // The one people misread: `exists` passing means ONE request carried it, not
  // that they all did. Spelling it out is the whole reason this line exists.
  exists: 'on at least one request',
  absent: 'on no request',
  count: 'counted across the session',
  ratio: 'as a rate across the session',
  noDuplicates: 'never repeating across the session',
  minInterval: 'spaced apart across the session',
  uniform: 'the same value across the session',
  coherent: 'agreeing across every source below',
};

/**
 * ruleExpression(check) -> "?iu= present · on every request"
 *
 * The whole rule as ONE line. Reads / Passes when / Mode were three rows
 * saying one thing, and a reader had to reassemble them mentally to answer the
 * only question they had: what exactly does this look for?
 *
 * Returns null for the shapes a single line would misrepresent — coherent
 * (several sources) and the aggregate modes (their read lives in probe-,
 * keyParams- or of-shaped fields). Those fall back to rows.
 */
export function ruleExpression(check) {
  const a = (check && check.assert) || {};
  if (!a.op || a.mode === 'coherent') return null;
  const where = describeSource(a.source, a.param);
  if (!where) return null;
  const scope = MODE_GLOSS[a.mode] || MODE_GLOSS.each;
  return `${where} ${describeExpectation(a)} · ${scope}`;
}

export function readRows(check) {
  const a = (check && check.assert) || {};

  // A coherent check reads several places, so it gets one row per probe — a
  // single Source line could not express it honestly.
  if (a.mode === 'coherent' && Array.isArray(a.probes)) {
    return a.probes.map((pr, i) => ({
      label: pr.label || `probe ${i + 1}`,
      value: describeSource(pr.source, pr.param),
    }));
  }

  // The session-level modes do NOT carry a top-level source/param — each keeps
  // its read somewhere mode-specific. Reading `a.source` for these would render
  // an empty Source line, which says "this rule reads nothing" about a rule
  // that reads plenty.
  if (a.mode === 'noDuplicates' && Array.isArray(a.keyParams)) {
    return a.keyParams.map((k, i) => ({
      label: a.keyParams.length > 1 ? `Key part ${i + 1}` : 'Key',
      value: describeSource(k && k.source, k && k.param),
    }));
  }

  if (a.mode === 'minInterval') {
    const g = a.groupBy || {};
    return [
      { label: 'Per', value: describeSource(g.source, g.param) },
      { label: 'Minimum gap', value: `${a.ms} ms` },
    ];
  }

  if (a.mode === 'ratio') {
    const of = a.of || {};
    return [
      { label: 'Counts requests where', value: `${describeSource(of.source, of.param)}  ${describeExpectation(of)}` },
      { label: 'Out of', value: 'every matching request' },
    ];
  }

  if (a.mode === 'absent' && Array.isArray(a.allow)) {
    return [
      { label: 'Source', value: describeSource(a.source, a.param) },
      { label: 'Allowed keys', value: a.allow.join(', ') },
    ];
  }

  const rows = [{ label: 'Source', value: describeSource(a.source, a.param) }];
  if (a.groupBy) {
    const g = a.groupBy;
    rows.push({
      label: 'Grouped by',
      value: typeof g === 'string' ? g : describeSource(g.source, g.param),
    });
  }
  return rows;
}


/* ── the rule's NAME ──────────────────────────────────────────────────────
 * `baseline-gam-iu` is a file-system handle, not a name. Inside a section
 * already titled "GAM request" it repeats the category twice and buries the
 * one token that identifies the rule: the parameter it reads.
 *
 * So the name IS the key, spelled exactly as the wire spells it:
 *   ?iu=            -> iu
 *   OpenRTB body    -> app.bundle, imp.banner.format   (full path, not the
 *                      leaf — `bundle` and `format` are ambiguous on their own
 *                      and the path is what you would grep the body for)
 *   cust_params     -> cust_params.auth
 *   a validator code-> id_leak_lmt
 *
 * The full id is never thrown away — it is the stable handle quarantine,
 * tickets and launch criteria key on — it just moves to the title attribute.
 */

// Ops that judge a VALUE rather than presence. Two rules on one parameter is
// the common shape (is `sz` there / is `sz` well-formed) and they need
// different names.
const VALUE_OPS = new Set([
  'validValue', 'oneOf', 'equals', 'matches', 'notMatches', 'range', 'gt', 'lt', 'inBucket',
]);

// Where the read lives differs by mode — see readRows for the same fan-out.
function primaryRead(a) {
  if (!a) return null;
  if (a.source && a.param) return { source: a.source, param: a.param };
  if (Array.isArray(a.probes) && a.probes[0]) return a.probes[0];
  if (Array.isArray(a.keyParams) && a.keyParams[0]) return a.keyParams[0];
  if (a.of && a.of.param) return a.of;
  if (a.groupBy && a.groupBy.param) return a.groupBy;
  return null;
}

function keyFor(source, param) {
  const p = String(param || '');
  if (!p) return '';
  switch (source) {
    // A query key is one token; the path form would invent structure that is
    // not in the URL.
    case 'url': case 'find': return p.split('.').pop();
    case 'custParams': return `cust_params.${p}`;
    // Body/response reads keep the FULL path: it is the WIRE CONTRACT, and
    // `app.bundle` is what you would search the payload for — `bundle` alone is
    // ambiguous.
    case 'body': case 'response': return p;
    // validData is the opposite: a synthetic namespace the proxy normalises
    // INTO, not something present on the wire. Nobody greps a payload for
    // `vastResponse.integrity.adsWithoutImpression`, and carrying the chain
    // makes a label three times longer than the column. The leaf is the answer.
    case 'validData': return p.split('.').pop();
    case 'issues': return p;
    default: return p;
  }
}

/** Humanise a check id once the category has already been said. */
function fromId(id, category) {
  let slug = String(id || '').replace(/^baseline-/, '');
  // Drop a leading vendor token the section header already carries.
  const catWords = String(category || '').toLowerCase().replace(/[^a-z]+/g, ' ').split(' ').filter(Boolean);
  const parts = slug.split('-');
  while (parts.length > 1 && catWords.includes(parts[0].toLowerCase())) parts.shift();
  slug = parts.join(' ');
  return slug || String(id || '');
}

/**
 * ruleName(check) -> "iu" | "sz values" | "app.bundle" | "url encoding clean"
 * Never empty; falls back to the id so a rule can always be referred to.
 */
export function ruleName(check) {
  // An authored `displayName` wins. Deriving the name from the read key is
  // right for the ~95% of rules that read one parameter, but it fails two ways:
  // two rules can read the SAME key for different questions (seatbid present vs
  // seatbid rate), and a response/validData path can be long enough to be
  // useless as a label (ad_networks.content_url_opted_out). Rather than bolt
  // more heuristics onto the deriver, let those rules say what they are.
  const authored = check && typeof check.displayName === 'string' && check.displayName.trim();
  if (authored) return authored;
  const a = (check && check.assert) || {};
  const read = primaryRead(a);
  const key = read ? keyFor(read.source, read.param) : '';
  if (!key) return fromId(check && check.id, check && check.category);
  return VALUE_OPS.has(a.op) ? `${key} values` : key;
}

/**
 * nameRules(checks) -> Map<checkId, string>
 *
 * ruleName() alone can collide: two rules can read the same key for different
 * reasons (`output values` on the GAM tag and on the IMA tag; `app.bundle` as
 * an OpenRTB read and as a coherence probe). A list with two identically
 * named rows is worse than the ids it replaced, so collisions get the token
 * from their id that actually distinguishes them appended in parentheses.
 */
export function nameRules(checks) {
  const list = Array.isArray(checks) ? checks : [];
  const base = new Map(list.map((c) => [c.id, ruleName(c)]));

  const counts = new Map();
  for (const n of base.values()) counts.set(n, (counts.get(n) || 0) + 1);

  const out = new Map();
  for (const c of list) {
    const n = base.get(c.id);
    if ((counts.get(n) || 0) < 2) { out.set(c.id, n); continue; }
    // Find the id token not shared with the other rules of the same name.
    const mine = String(c.id).replace(/^baseline-/, '').split('-');
    const others = list
      .filter((o) => o.id !== c.id && base.get(o.id) === n)
      .map((o) => new Set(String(o.id).replace(/^baseline-/, '').split('-')));
    const distinct = mine.find((t) => others.every((set) => !set.has(t)));
    out.set(c.id, distinct ? `${n} (${distinct})` : `${n} (${c.id})`);
  }
  return out;
}
